import { AutocompleteOption } from 'blip-ds/dist/types/components/autocomplete/autocomplete-select-interface';
import { ALL_GROUPS_ID, DEFAULT_GROUP_INDEX, NO_GROUP_ID, STORAGE_KEY } from '../constants/utils';
import i18n from '../i18n/i18n';
import { Installation } from '../interfaces';
import { createToastError } from '../services/Toast';

export const isInstallationWithoutGroup = (installation: Installation): boolean => {
    return installation.workGroupId === null || installation.workGroupId === undefined;
};

export const filterInstallationsByGroup = (
    groupId: number,
    installations: Installation[],
    groupHierarchy: Map<number, number[]>,
): Installation[] => {
    if (groupId === NO_GROUP_ID) {
        return installations.filter(isInstallationWithoutGroup);
    }
    if (groupId === ALL_GROUPS_ID) {
        return installations;
    }

    const childGroupIds = groupHierarchy.get(groupId);

    if (childGroupIds && childGroupIds.length > 0) {
        return installations.filter(installation => childGroupIds.includes(installation.workGroupId));
    }

    return installations.filter(installation => installation.workGroupId === groupId);
};

export const createNumberOptions = (installations: Installation[]): AutocompleteOption[] => {
    return installations.map(installation => ({
        label: installation.installationName,
        value: installation.installationId.toString(),
        checked: true,
    }));
};

export const findSelectedInstallations = (
    selectedOptions: AutocompleteOption[],
    allInstallations: Installation[],
): Installation[] => {
    return selectedOptions
        .map(option => {
            const installationId = parseInt(option.value);
            return allInstallations.find(installation => installation.installationId === installationId);
        })
        .filter((installation): installation is Installation => installation !== undefined);
};

export const updateNumberOptionsWithSelection = (
    availableInstallations: Installation[],
    selectedOptions: AutocompleteOption[],
): AutocompleteOption[] => {
    return availableInstallations.map(installation => ({
        label: installation.installationName,
        value: installation.installationId.toString(),
        checked: selectedOptions.some(option => option.value === installation.installationId.toString()),
    }));
};

// Helper functions for managing installation storage and selection
export const getStoredGroupId = (): string | null => localStorage.getItem(STORAGE_KEY);

export const setStoredGroupId = (groupId: number): void => localStorage.setItem(STORAGE_KEY, String(groupId));

export const pickInitialGroupId = (groupIds: number[]): number | null => {
    const stored = getStoredGroupId();

    if (stored) {
        const parsed = Number(stored);
        if (groupIds.includes(parsed)) return parsed;
    }

    return groupIds.length > DEFAULT_GROUP_INDEX ? groupIds[DEFAULT_GROUP_INDEX] : ALL_GROUPS_ID;
};

export const buildInitialSelection = (
    groupId: number | null,
    allInstallations: Installation[],
    groupsOfGroups: Map<number, number[]>,
) => {
    if (groupId === null) {
        return {
            groupInstallations: [] as Installation[],
            initialNumberOptions: [] as AutocompleteOption[],
        };
    }

    const groupInstallations = filterInstallationsByGroup(groupId, allInstallations, groupsOfGroups);
    const initialNumberOptions = createNumberOptions(groupInstallations).map(option => ({
        ...option,
        checked: true,
    }));

    return { groupInstallations, initialNumberOptions };
};

export const handleFetchError = (error: unknown, context: string): void => {
    console.error(`Error ${context}:`, error);
    createToastError(
        context === 'fetching installations'
            ? i18n.t('go-personal-error-fetch-numbers-title')
            : i18n.t('go-personal-error-internal-title'),
        i18n.t('go-personal-error-retry-message'),
    );
};
